import { inject, injectable } from 'inversify'
import { action, computed, makeObservable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { ViewportGateway } from '@core/services/viewport.gateway'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { PageTitleConfig } from '@core/infra/page-title/page-title.config'
import { UserModel } from '@core/infra/authentication/user.model'
import { ViewportConfig } from '@core/infra/viewport/viewport.config'
import { ShellRepository, type SidebarMode } from './shell.repository'

export type { SidebarMode }

@injectable()
export class ShellPresenter implements AppLifecycle {
  @inject(ShellRepository)
  private shellRepository!: ShellRepository

  @inject(Types.IViewportGateway)
  private viewportGateway!: ViewportGateway

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  // The brand, from the same config the tab title composes with — it was a
  // literal in the top bar, which meant two places to change one name.
  @inject(PageTitleConfig)
  private pageTitleConfig!: PageTitleConfig

  @inject(UserModel)
  private userModel!: UserModel

  @inject(ViewportConfig)
  private viewportConfig!: ViewportConfig

  constructor() {
    makeObservable(this, {
      sidebarMode: computed,
      viewModel: computed,
      toggleSidebar: action,
    })
  }

  get sidebarMode(): SidebarMode {
    return this.shellRepository.sidebarModePm
  }

  // Every display condition resolved to a boolean and every label pre-formatted
  // (machine-architecture.md §3.4/§4.3) — components read this, not `sidebarMode`.
  get viewModel() {
    const isClosed = this.sidebarMode === 'closed'
    // A share-link outsider has exactly one destination — the shared job — so
    // the whole navigation surface (sidebar, its toggle, search) is hidden for
    // them. See UserModel.isExternal.
    const showChrome = !this.userModel.isExternal

    return {
      showChrome,
      showSidebar: showChrome && !isClosed,
      sidebarOpen: !isClosed,
      toggleSidebarLabel: this.i18nRepository.t(isClosed ? 'shell.showNavigation' : 'shell.hideNavigation'),
      searchLabel: this.i18nRepository.t('shell.search'),
      appName: this.pageTitleConfig.appName,
    }
  }

  // Called once from AppPresenter.load(). First-ever visit (nothing persisted
  // yet) defaults open on desktop, closed on mobile (would otherwise cover
  // the whole screen as an overlay on first paint) — after that, whatever the
  // user last explicitly toggled to wins, even if it now "disagrees" with the
  // viewport (e.g. manually closed on desktop stays closed on reload).
  initialize() {
    const viewportDefault: SidebarMode = this.viewportGateway.matches(this.viewportConfig.desktopQuery)
      ? 'expanded'
      : 'closed'
    this.shellRepository.restore(viewportDefault)
  }

  toggleSidebar() {
    this.shellRepository.setSidebarMode(this.sidebarMode === 'closed' ? 'expanded' : 'closed')
  }
}
